import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../../supabase-client";
import useAdministradores from "../../components/getadministradores";
import Enviarmail from "../../components/enviarmail";

const Success = () => {
   // ================================
   // HOOKS & STATE MANAGEMENT
   // ================================
   const { administradores, loading: adminLoading } = useAdministradores();
   const location = useLocation();
   const navigate = useNavigate();

   // State variables
   const [mailAdmin, setMailAdmin] = useState("");
   const [paymentData, setPaymentData] = useState(null);
   const [orderData, setOrderData] = useState(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);

   // ================================
   // UTILITY FUNCTIONS
   // ================================
   const extractUrlParams = (search) => {
      const urlParams = new URLSearchParams(search);
      return {
         pedido: urlParams.get("pedido") || 0,
         preference_id: urlParams.get("preference_id"),
         cotizacion_id: urlParams.get("collection_id") || 0,
         pago: urlParams.get("pago"),
      };
   };

   const generateAdminEmails = (adminList) => {
      if (!adminList || adminList.length === 0) return "";
      
      return adminList
         .filter(admin => admin.email && admin.email.trim())
         .map(admin => admin.email.trim())
         .join(",");
   };

   // ================================
   // DATABASE OPERATIONS
   // ================================
   const updateOrderStatus = async (paymentData) => {
      console.log("📝 Updating order status:", paymentData);

      try {
         if (paymentData.pedido !== 0) {
            // Update pedidos table
            const { error } = await supabase
               .from("pedidos")
               .update({ estado: paymentData.pago })
               .eq("id", paymentData.pedido);

            if (error) throw error;
            console.log("✅ Pedido status updated successfully");

         } else {
            // Update cotizacion_pagos and cotizacion tables
            await Promise.all([
               supabase
                  .from("cotizacion_pagos")
                  .update({ estado: paymentData.pago })
                  .eq("cotizacion_id", paymentData.cotizacion_id),
               supabase
                  .from("cotizacion")
                  .update({ estado: paymentData.pago })
                  .eq("id", paymentData.cotizacion_id)
            ]);

            console.log("✅ Cotización status updated successfully");
         }
      } catch (error) {
         console.error("❌ Error updating order status:", error);
         throw error;
      }
   };

   const insertPedidosHist = async (paymentData) => {
      console.log("📝 Inserting pedidos history:", paymentData);

      try {
         const { error } = await supabase
            .from("pedidos_hist")
            .insert({
               pedido_id: paymentData.pedido,
               estado: "pagado",
            });

         if (error) throw error;
         console.log("✅ Pedidos history inserted successfully");
      } catch (error) {
         console.error("❌ Error inserting pedidos history:", error);
         throw error;
      }
   };

   const insertMercadoPago = async (paymentData) => {
      console.log("📝 Inserting MercadoPago record:", paymentData);

      try {
         const { error } = await supabase
            .from("mercado_pago")
            .insert({
               mercadopago_id: paymentData.preference_id,
               pedido_id: paymentData.pedido || 0,
               cotizacion_id: paymentData.cotizacion_id || 0,
               estado: paymentData.pago,
            });

         if (error) throw error;
         console.log("✅ MercadoPago record inserted successfully");
      } catch (error) {
         console.error("❌ Error inserting MercadoPago record:", error);
         throw error;
      }
   };

   const fetchOrderDetails = async (orderId) => {
      console.log("📦 Fetching order details:", orderId);

      try {
         const { data, error } = await supabase
            .from("pedidos")
            .select(`
               *,
               usuarios:usuario_id (
                  nombre,
                  email,
                  celular
               )
            `)
            .eq("id", orderId)
            .single();

         if (error) throw error;

         setOrderData(data);
         console.log("✅ Order details loaded:", data);
         return data;
      } catch (error) {
         console.error("❌ Error fetching order details:", error);
         throw error;
      }
   };

   // ================================
   // EMAIL NOTIFICATIONS
   // ================================
   const sendSuccessNotification = async (paymentData) => {
      console.log("📧 Sending customer notification:", paymentData);

      if (!orderData?.usuarios?.email) {
         console.warn("⚠️ No customer email available");
         return;
      }

      try {
         const response = await fetch(
            `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-mail`,
            {
               method: "POST",
               headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
               },
               body: JSON.stringify({
                  to: orderData.usuarios.email,
                  subject: "✅ Pago Confirmado - Diego Curutchet",
                  message: `
                     <h1>¡Pago Confirmado!</h1>
                     <p>Tu pago ha sido procesado exitosamente.</p>
                     <p><strong>Detalles del pago:</strong></p>
                     <ul>
                        <li>ID de Pago: ${paymentData.payment_id}</li>
                        <li>Estado: Pagado</li>
                        <li>Fecha: ${new Date().toLocaleString()}</li>
                     </ul>
                     <p>Te contactaremos pronto para coordinar la entrega.</p>
                  `,
               }),
            }
         );

         if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
         }

         console.log("✅ Customer notification sent successfully");
      } catch (error) {
         console.error("❌ Error sending customer notification:", error);
      }
   };

   const sendSuccessCotizacionNotification = async (paymentData) => {
      console.log("📧 Sending admin notification:", { mailAdmin, paymentData });

      if (!mailAdmin) {
         console.warn("⚠️ No admin emails available");
         return;
      }

      try {
         const emailData = {
            aquien: mailAdmin,
            reply_to: mailAdmin,
            asunto: `💰 Pago de Anticipo - Cotización #${paymentData.cotizacion_id}`,
            texto: `
               <h2>💰 Pago de Anticipo Recibido</h2>
               <p>Se ha abonado el anticipo de la cotización número <b>${paymentData.cotizacion_id}</b></p>
               <p><strong>Detalles:</strong></p>
               <ul>
                  <li>ID Cotización: ${paymentData.cotizacion_id}</li>
                  <li>Tipo de pago: ${paymentData.pago}</li>
                  <li>Fecha: ${new Date().toLocaleString()}</li>
               </ul>
               <p><a href="${window.location.origin}/admcuru/cotizacionadmin" 
                     style="background-color: #3B82F6; color: white; padding: 10px 20px; 
                            text-decoration: none; border-radius: 5px;">
                  Ver Cotización
               </a></p>
               <p>Sistema Diego Curutchet</p>
            `,
         };

         const resp = await Enviarmail(emailData);

         if (resp && resp.success !== false) {
            console.log("✅ Admin notification sent successfully");
         } else {
            console.error("❌ Failed to send admin notification:", resp);
         }
      } catch (error) {
         console.error("❌ Error sending admin notification:", error);
      }
   };

   // ================================
   // MAIN PROCESSING LOGIC
   // ================================
   const processSuccessData = async () => {
      try {
         // Extract URL parameters
         const successData = extractUrlParams(location.search);
         console.log("🎉 Processing payment success data:", successData);
         setPaymentData(successData);

         // Update database records
         await updateOrderStatus(successData);
         await insertMercadoPago(successData);

         // Handle pedido-specific operations
         if (successData.pedido > 0) {
            await insertPedidosHist(successData);
            await fetchOrderDetails(successData.pedido);
            
            if (successData.payment_id) {
               await sendSuccessNotification(successData);
            }
         }

         // Handle cotización-specific operations
         if (successData.cotizacion_id > 0 && mailAdmin) {
            await sendSuccessCotizacionNotification(successData);
         }

      } catch (error) {
         console.error("❌ Error processing success data:", error);
         setError(error.message);
      } finally {
         setLoading(false);
      }
   };

   // ================================
   // NAVIGATION HANDLERS
   // ================================
   // const handleContinueShopping = () => {
   //    navigate("/obras");
   // };

   const handleGoToInicio = () => {
      navigate("/");
   };

   const handleGoHome = () => {
      navigate("/");
   };

   // ================================
   // EFFECTS
   // ================================
   // Generate admin emails when administrators data is loaded
   useEffect(() => {
      if (administradores && administradores.length > 0) {
         const adminEmails = generateAdminEmails(administradores);
         console.log("📧 Admin emails generated:", adminEmails);
         setMailAdmin(adminEmails);
      }
   }, [administradores]);

   // Process success data when all required data is available
   useEffect(() => {
      if (!adminLoading) {
         processSuccessData();
      }
   }, [location.search, adminLoading, mailAdmin]);

   // ================================
   // RENDER COMPONENTS
   // ================================
   const LoadingComponent = () => (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
         <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-lg text-gray-600">Confirmando tu pago...</p>
         </div>
      </div>
   );

   const ErrorComponent = () => (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
         <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
            <div className="text-red-500 text-6xl mb-4">❌</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Error</h1>
            <p className="text-gray-600 mb-6">
               Hubo un error procesando la confirmación: {error}
            </p>
            <button
               onClick={handleGoHome}
               className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg transition-colors"
            >
               Ir al Inicio
            </button>
         </div>
      </div>
   );

   const PaymentDetailsSection = () => (
      paymentData && (
         <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
               Detalles del Pago:
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
               {paymentData.payment_id && (
                  <DetailRow 
                     label="ID de Pago" 
                     value={paymentData.payment_id} 
                  />
               )}
               {paymentData.status && (
                  <DetailRow 
                     label="Estado" 
                     value={paymentData.status}
                     valueClass="text-green-600 font-semibold" 
                  />
               )}
               {paymentData.external_reference && (
                  <DetailRow 
                     label="Orden" 
                     value={`#${paymentData.external_reference}`} 
                  />
               )}
               {paymentData.payment_type && (
                  <DetailRow 
                     label="Método" 
                     value={paymentData.payment_type} 
                  />
               )}
            </div>
         </div>
      )
   );

   const OrderDetailsSection = () => (
      orderData && (
         <div className="bg-blue-50 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
               Detalles de tu Pedido:
            </h3>
            <div className="space-y-2 text-sm">
               <DetailRow 
                  label="Cliente" 
                  value={orderData.usuarios?.nombre} 
               />
               <DetailRow 
                  label="Email" 
                  value={orderData.usuarios?.email} 
               />
               <DetailRow 
                  label="Total" 
                  value={`$${orderData.total?.toLocaleString()}`}
                  valueClass="text-2xl font-bold text-green-600" 
               />
               <DetailRow 
                  label="Fecha" 
                  value={new Date(orderData.fecha_pedido).toLocaleString()} 
               />
            </div>
         </div>
      )
   );

   const DetailRow = ({ label, value, valueClass = "text-gray-800" }) => (
      <div>
         <span className="font-medium text-gray-600">{label}:</span>
         <span className={`ml-2 ${valueClass}`}>{value}</span>
      </div>
   );

   // const NextStepsSection = () => (
   //    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
   //       <h4 className="font-semibold text-yellow-800 mb-2">
   //          📋 Próximos Pasos:
   //       </h4>
   //       <ul className="text-sm text-yellow-700 space-y-1">
   //          <li>• Recibirás un email de confirmación</li>
   //          <li>• Te contactaremos para coordinar la entrega</li>
   //          <li>• Puedes ver el estado de tu pedido en "Mis Pedidos"</li>
   //       </ul>
   //    </div>
   // );

   const ActionButtonsSection = () => (
      <div className="flex flex-col sm:flex-row gap-4">
         <button
            onClick={handleGoToInicio}
            className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg transition-colors"
         >
            Ir a Inicio
         </button>
      </div>
   );

   const SupportSection = () => (
      <div className="mt-8 text-center text-sm text-gray-500">
         <p>¿Tienes preguntas? Contáctanos:</p>
         <p>Email: soporte@diegocurutchet.com | Tel: +54 123 456 7890</p>
      </div>
   );

   // ================================
   // MAIN RENDER
   // ================================
   if (loading) return <LoadingComponent />;
   if (error) return <ErrorComponent />;

   return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center py-12 px-4">
         <div className="max-w-2xl w-full bg-white shadow-xl rounded-lg overflow-hidden">
            {/* Success Header */}
            <div className="bg-green-500 text-white p-8 text-center">
               <div className="text-6xl mb-4">🎉</div>
               <h1 className="text-3xl font-bold mb-2">¡Pago Exitoso!</h1>
            </div>

            {/* Main Content */}
            <div className="p-8">
               <PaymentDetailsSection />
               <OrderDetailsSection />
               {/* {successData.pedido > 0 ?(
                 <NextStepsSection />
               ) : null} */}
               <ActionButtonsSection />
               <SupportSection />
            </div>
         </div>
      </div>
   );
};

export default Success;